import React, { useState, useEffect } from "react";
import { useAuth } from "../../../context/AuthContext";
import { authApi } from "../../../api/authApi";
import "./ProfileSettings.css";

// Same password rules as the Register page
const PASSWORD_RULES = [
  { key: "length", label: "At least 8 characters", test: (p) => p.length >= 8 },
  {
    key: "uppercase",
    label: "At least 1 uppercase letter",
    test: (p) => /[A-Z]/.test(p),
  },
  {
    key: "lowercase",
    label: "At least 1 lowercase letter",
    test: (p) => /[a-z]/.test(p),
  },
  { key: "digit", label: "At least 1 number", test: (p) => /\d/.test(p) },
  {
    key: "special",
    label: "At least 1 special character (!@#$%^&*)",
    test: (p) => /[!@#$%^&*(),.?":{}|<>]/.test(p),
  },
];

const getStrength = (met) => {
  if (met === 0) return { text: "", level: "none" };
  if (met === PASSWORD_RULES.length) return { text: "Strong", level: "strong" };
  if (met >= 3) return { text: "Medium", level: "medium" };
  return { text: "Weak", level: "weak" };
};

const getInitials = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "?";

const ProfileSettings = () => {
  const { user, updateUser } = useAuth();
  const [formData, setFormData] = useState({
    fullName: "",
    mobileNumber: "",
  });
  const [passwordData, setPasswordData] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [showPasswords, setShowPasswords] = useState(false);
  const [loading, setLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [passwordMessage, setPasswordMessage] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [communicationPreferences, setCommunicationPreferences] = useState(null);
  const [optionalUpdatesEnabled, setOptionalUpdatesEnabled] = useState(false);
  const [communicationsLoading, setCommunicationsLoading] = useState(true);
  const [communicationsSaving, setCommunicationsSaving] = useState(false);
  const [deletionLoading, setDeletionLoading] = useState(false);
  const [privacyMessage, setPrivacyMessage] = useState("");
  const [privacyError, setPrivacyError] = useState("");

  useEffect(() => {
    if (user) {
      setFormData({
        fullName: user.FullName || "",
        mobileNumber: user.MobileNumber || "",
      });
    }
  }, [user]);

  useEffect(() => {
    if (!user || user.Role !== "client" || user.SupportSession) {
      setCommunicationsLoading(false);
      return;
    }
    let cancelled = false;
    authApi
      .getCommunicationPreferences()
      .then((response) => {
        if (cancelled) return;
        const data = response.data || {};
        setCommunicationPreferences(data);
        setOptionalUpdatesEnabled(
          Boolean(data.OptionalEmailUpdatesEnabled),
        );
      })
      .catch((loadError) => {
        if (!cancelled) {
          setPrivacyError(
            loadError.response?.data?.error ||
              "Could not load email and privacy settings.",
          );
        }
      })
      .finally(() => {
        if (!cancelled) setCommunicationsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({ ...prev, [name]: value }));
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setMessage("");

    try {
      await authApi.updateProfile(formData);
      setMessage("Profile updated successfully.");
      updateUser({
        ...user,
        FullName: formData.fullName,
        MobileNumber: formData.mobileNumber,
      });
    } catch (err) {
      setError(err.response?.data?.error || "Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordError("");
    setPasswordMessage("");

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError("New passwords do not match");
      return;
    }

    const isSupportSession = Boolean(user?.SupportSession);
    if (!PASSWORD_RULES.every((rule) => rule.test(passwordData.newPassword))) {
      setPasswordError("New password does not meet the requirements below");
      return;
    }
    if (!isSupportSession && !passwordData.oldPassword) {
      setPasswordError("Current password is required");
      return;
    }

    setPasswordLoading(true);

    try {
      if (isSupportSession) {
        const result = await authApi.changeClientPasswordDuringSupport(
          passwordData.newPassword,
        );
        updateUser({ MustChangePassword: true });
        setPasswordMessage(
          result.message ||
            "Password changed. The client will be asked to choose a new password at their next sign in.",
        );
      } else {
        await authApi.changePassword(
          passwordData.oldPassword,
          passwordData.newPassword,
        );
        setPasswordMessage("Password changed successfully.");
      }
      setPasswordData({
        oldPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    } catch (err) {
      setPasswordError(
        err.response?.data?.error || "Failed to change password",
      );
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleSaveCommunications = async (event) => {
    event.preventDefault();
    setCommunicationsSaving(true);
    setPrivacyError("");
    setPrivacyMessage("");
    try {
      const response = await authApi.updateCommunicationPreferences(
        optionalUpdatesEnabled,
      );
      setCommunicationPreferences(response.data);
      setPrivacyMessage(response.message || "Email preferences saved.");
    } catch (saveError) {
      setPrivacyError(
        saveError.response?.data?.error || "Could not save email preferences.",
      );
    } finally {
      setCommunicationsSaving(false);
    }
  };

  const handleRequestDeletion = async () => {
    setDeletionLoading(true);
    setPrivacyError("");
    setPrivacyMessage("");
    try {
      const response = await authApi.requestAccountDeletion();
      setCommunicationPreferences((current) => ({
        ...current,
        ...response.data,
      }));
      setPrivacyMessage(response.message);
    } catch (requestError) {
      setPrivacyError(
        requestError.response?.data?.error ||
          "Could not submit your account deletion request.",
      );
    } finally {
      setDeletionLoading(false);
    }
  };

  const handleCancelDeletion = async () => {
    setDeletionLoading(true);
    setPrivacyError("");
    setPrivacyMessage("");
    try {
      const response = await authApi.cancelAccountDeletionRequest();
      setCommunicationPreferences((current) => ({
        ...current,
        DeletionRequestID: null,
        DeletionRequestStatus: null,
        DeletionRequestedAt: null,
      }));
      setPrivacyMessage(response.message);
    } catch (cancelError) {
      setPrivacyError(
        cancelError.response?.data?.error ||
          "Could not cancel your account deletion request.",
      );
    } finally {
      setDeletionLoading(false);
    }
  };

  const { newPassword, confirmPassword } = passwordData;
  const rules = PASSWORD_RULES.map((rule) => ({
    label: rule.label,
    met: rule.test(newPassword),
  }));
  const metCount = rules.filter((rule) => rule.met).length;
  const strength = getStrength(metCount);
  const showMatch = confirmPassword.length > 0;
  const passwordsMatch = newPassword === confirmPassword;

  const passwordType = showPasswords ? "text" : "password";

  return (
    <div className="profile-settings-page">
      <header className="ps-header">
        <div className="ps-avatar" aria-hidden="true">
          {getInitials(formData.fullName || user?.FullName)}
        </div>
        <div className="ps-header-text">
          <h2>Profile settings</h2>
          <p>
            {user?.Email
              ? `${user.Email} · Manage your account information`
              : "Manage your account information"}
          </p>
        </div>
      </header>

      <div className="ps-grid">
        {/* Profile form */}
        <section
          className="dashboard-card ps-card"
          aria-labelledby="ps-personal"
        >
          <div className="ps-card-head">
            <h3 id="ps-personal">Personal information</h3>
            <p>Your name and the number we use to reach you.</p>
          </div>

          {message && (
            <div className="alert alert-success" role="status">
              {message}
            </div>
          )}
          {error && (
            <div className="alert alert-error" role="alert">
              {error}
            </div>
          )}

          <form onSubmit={handleProfileSubmit}>
            <div className="form-group">
              <label htmlFor="fullName">Full name</label>
              <input
                type="text"
                id="fullName"
                name="fullName"
                autoComplete="name"
                value={formData.fullName}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="mobileNumber">Mobile number</label>
              <input
                type="tel"
                id="mobileNumber"
                name="mobileNumber"
                autoComplete="tel"
                value={formData.mobileNumber}
                onChange={handleChange}
                required
              />
            </div>

            <div className="ps-actions">
              <button type="submit" className="btn-primary" disabled={loading}>
                {loading ? "Saving..." : "Save changes"}
              </button>
            </div>
          </form>
        </section>

        {!user?.SupportSession && user?.Role === "client" && (
          <section
            className="dashboard-card ps-card ps-privacy-card"
            aria-labelledby="ps-communications"
          >
            <div className="ps-card-head">
              <h3 id="ps-communications">Email and privacy preferences</h3>
              <p>
                Manage optional updates and submit a request to permanently
                delete your account.
              </p>
            </div>
            {privacyMessage && (
              <div className="alert alert-success" role="status">
                {privacyMessage}
              </div>
            )}
            {privacyError && (
              <div className="alert alert-error" role="alert">
                {privacyError}
              </div>
            )}

            <form onSubmit={handleSaveCommunications}>
              <label className="ps-communications-toggle">
                <input
                  type="checkbox"
                  checked={optionalUpdatesEnabled}
                  onChange={(event) =>
                    setOptionalUpdatesEnabled(event.target.checked)
                  }
                  disabled={communicationsLoading || communicationsSaving}
                />
                <span>
                  <strong>Optional newsletters and product updates</strong>
                  <small>
                    Promotions and non-essential announcements. You can
                    withdraw consent at any time.
                  </small>
                </span>
              </label>
              <p className="ps-essential-notice">
                Unsubscribing does not stop essential account, security,
                payment, onboarding, maintenance, service interruption, or
                shutdown notices while your account or service remains active.
              </p>
              <div className="ps-actions">
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={communicationsLoading || communicationsSaving}
                >
                  {communicationsSaving ? "Saving..." : "Save email preferences"}
                </button>
              </div>
            </form>

            <div className="ps-deletion-request">
              <h4>Request account deletion</h4>
              <p>
                Your request will be reviewed before any permanent deletion.
                Information may need to be retained for legal, accounting,
                payment, contractual, security, or dispute-resolution
                obligations. Submitting a request does not immediately close
                your account or cancel amounts due.
              </p>
              {communicationPreferences?.DeletionRequestStatus === "pending" ? (
                <div className="ps-deletion-status">
                  <span>Your deletion request is awaiting review.</span>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={handleCancelDeletion}
                    disabled={deletionLoading}
                  >
                    {deletionLoading ? "Cancelling..." : "Cancel request"}
                  </button>
                </div>
              ) : communicationPreferences?.DeletionRequestStatus ===
                "approved" ? (
                <p className="ps-deletion-status">
                  Your request has been approved and is awaiting completion.
                  Please contact support if you have questions.
                </p>
              ) : (
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={handleRequestDeletion}
                  disabled={deletionLoading || communicationsLoading}
                >
                  {deletionLoading
                    ? "Submitting request..."
                    : "Submit deletion request"}
                </button>
              )}
            </div>
          </section>
        )}

        {/* Change password */}
        <section
          className="dashboard-card ps-card"
          aria-labelledby="ps-password"
        >
          {user?.MustChangePassword && (
            <div className="alert alert-error" role="alert">
              An administrator issued a temporary password for your account.
              Change it below to continue using Toora Flex.
            </div>
          )}
          <div className="ps-card-head">
            <h3 id="ps-password">Change password</h3>
            <p>
              {user?.SupportSession
                ? "Set a temporary password for this client. They will be required to change it when they next sign in."
                : "Choose a new password to keep your account secure."}
            </p>
          </div>

          {passwordMessage && (
            <div className="alert alert-success" role="status">
              {passwordMessage}
            </div>
          )}
          {passwordError && (
            <div className="alert alert-error" role="alert">
              {passwordError}
            </div>
          )}

          <form onSubmit={handlePasswordSubmit}>
            {!user?.SupportSession && (
              <div className="form-group">
                <label htmlFor="oldPassword">Current password</label>
                <input
                  type={passwordType}
                  id="oldPassword"
                  name="oldPassword"
                  autoComplete="current-password"
                  value={passwordData.oldPassword}
                  onChange={handlePasswordChange}
                  required
                />
              </div>
            )}

            <div className="form-group">
              <label htmlFor="newPassword">New password</label>
              <input
                type={passwordType}
                id="newPassword"
                name="newPassword"
                autoComplete="new-password"
                value={passwordData.newPassword}
                onChange={handlePasswordChange}
                required
                aria-describedby="ps-rules"
              />
              {newPassword.length > 0 && (
                <div className={`ps-strength is-${strength.level}`}>
                  <div className="ps-strength-bar">
                    <div
                      className="ps-strength-fill"
                      style={{
                        width: `${(metCount / PASSWORD_RULES.length) * 100}%`,
                      }}
                    />
                  </div>
                  <span className="ps-strength-text">{strength.text}</span>
                </div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm new password</label>
              <input
                type={passwordType}
                id="confirmPassword"
                name="confirmPassword"
                autoComplete="new-password"
                value={passwordData.confirmPassword}
                onChange={handlePasswordChange}
                required
              />
              {showMatch && (
                <span
                  className={
                    passwordsMatch ? "ps-match is-success" : "ps-match is-error"
                  }
                  role="status"
                >
                  {passwordsMatch
                    ? "✓ Passwords match"
                    : "Passwords do not match"}
                </span>
              )}
            </div>

            <label className="ps-toggle">
              <input
                type="checkbox"
                checked={showPasswords}
                onChange={(e) => setShowPasswords(e.target.checked)}
              />
              <span>Show passwords</span>
            </label>

            <ul className="ps-rules" id="ps-rules" aria-label="Password rules">
              {rules.map((rule) => (
                <li
                  key={rule.label}
                  className={rule.met ? "ps-rule is-met" : "ps-rule"}
                >
                  <span className="ps-rule-icon" aria-hidden="true">
                    {rule.met ? "✓" : ""}
                  </span>
                  <span>{rule.label}</span>
                  <span className="ps-sr-only">
                    {rule.met ? " (met)" : " (not met yet)"}
                  </span>
                </li>
              ))}
            </ul>

            <div className="ps-actions">
              <button
                type="submit"
                className="btn-secondary"
                disabled={passwordLoading}
              >
                {passwordLoading ? "Changing..." : "Change password"}
              </button>
            </div>
          </form>
        </section>
      </div>
    </div>
  );
};

export default ProfileSettings;
